import { resizeCloudinary } from "../../../utils/util";
import DSBox from "../../system-design/DSBox";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSTypography from "../../system-design/DSTypography";
import PageHeader from "components/PageHeader";
import FilterBar from "components/FilterBar";
import { DSBaseTable, DSTableCell } from "../../system-design/Table";
import { Column } from "../../system-design/Table/DSBaseTable/components/DSBaseTable";
import { useCallback, useState } from "react";
import {
  debounce,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  useTheme,
} from "@mui/material";
import DSAvatar from "../../system-design/DSAvatar";
import useAppHomeEntriesQuery from "../hooks/useAppHomeEntriesQuery";
import useDeleteAppHomeEntryMutation from "../hooks/useDeleteAppHomeEntryMutation";
import {
  AppHomeEntry,
  AppHomeEntrySearchFilters,
} from "../../../api/appHomeEntry";
import FloatAddButton from "../../../components/Widgets/FloatAddButton";
import AppHomeEntryDialog from "./AppHomeEntryDialog";

const AppHomeEntries = () => {
  const [selectedRow, setSelectedRow] = useState<AppHomeEntry | null>(null);
  const [showEntryDialog, setShowEntryDialog] = useState<boolean>(false);
  const [query, setQuery] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");
  const [filterValues, setFilterValues] = useState<string[]>([]);
  const filters: AppHomeEntrySearchFilters = {
    active: filterValues.includes("Active") ? true : undefined,
  };

  const { entries, isLoading, fetchNextPage } = useAppHomeEntriesQuery({
    query,
    filters,
  });

  const { mutate: deleteEntry } = useDeleteAppHomeEntryMutation();

  const handleDeleteEntry = useCallback(
    (entry: AppHomeEntry) => {
      deleteEntry(entry.id);
    },
    [deleteEntry],
  );

  const onPressRow = useCallback(
    (row: AppHomeEntry) => {
      setSelectedRow(row);
      setShowEntryDialog(true);
    },
    [setSelectedRow],
  );
  const { spacing } = useTheme();

  const handleFreeSearch = useCallback(
    debounce((s) => setQuery(s), 500),
    [],
  );

  const columns: Column<AppHomeEntry>[] = [
    { field: "type", label: "Type" },
    { field: "pictureUrl", label: "Picture" },
    { field: "active", label: "Active" },
  ];

  const buildTableRows = (
    entry: AppHomeEntry,
    columns: Column<AppHomeEntry>[],
  ) => (
    <>
      {columns.map((col) => (
        <DSTableCell
          key={col.field as string}
          style={{
            width: col.size || `${100 / columns?.length}%`,
            whiteSpace: "nowrap",
          }}
        >
          {col.field === "pictureUrl" ? (
            <DSBox
              sx={{
                width: spacing(10),
                height: spacing(10),
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <DSAvatar src={resizeCloudinary(entry.pictureUrl, 96)} />
            </DSBox>
          ) : col.field === "active" ? (
            <DSTypography variant="body2">
              {entry.active ? "Yes" : "No"}
            </DSTypography>
          ) : (
            <DSTypography variant="body2">
              {entry[col.field as keyof AppHomeEntry]?.toString() || ""}
            </DSTypography>
          )}
        </DSTableCell>
      ))}
    </>
  );

  return (
    <DSBox>
      <PageHeader
        overline="Content"
        title="Entries"
        subtitle="Manage the content entries shown on the app's home screen."
      />
      <FilterBar
        search={{
          value: searchInput,
          onChange: (value) => {
            setSearchInput(value);
            handleFreeSearch(value);
          },
          placeholder: "Entry name",
        }}
        activeCount={filterValues.length}
      >
        <FormControl sx={{ minWidth: 130 }}>
          <InputLabel id="filter">Filter</InputLabel>
          <Select
            label="Filter"
            labelId="filter"
            multiple
            value={filterValues}
            onChange={(v) => {
              setFilterValues(
                typeof v.target.value === "string"
                  ? [v.target.value]
                  : v.target.value,
              );
            }}
          >
            <MenuItem key="Active" value="Active">
              Active
            </MenuItem>
          </Select>
        </FormControl>
      </FilterBar>
      {entries ? (
        <DSBaseTable
          data={entries}
          columns={columns}
          rowBuilder={buildTableRows}
          fetchMore={fetchNextPage}
          onPressRow={onPressRow}
          onPressDelete={handleDeleteEntry}
        />
      ) : (
        !isLoading && (
          <DSTypography variant="body1" textAlign="center">
            No Artist found
          </DSTypography>
        )
      )}
      {isLoading && (
        <DSBox
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <DSCircularProgress />
        </DSBox>
      )}
      <FloatAddButton
        onClick={() => {
          setSelectedRow(null);
          setShowEntryDialog(true);
        }}
      />
      {showEntryDialog && (
        <AppHomeEntryDialog
          open={showEntryDialog}
          onClose={() => {
            setShowEntryDialog(false);
            setSelectedRow(null);
          }}
          entry={selectedRow}
        />
      )}
    </DSBox>
  );
};

export default AppHomeEntries;
